import React from "react";

type Props = { children: React.ReactNode };

type State = { hasError: boolean; error?: Error };

/**
 * Global Error Boundary to catch React errors and display component stack
 * Helps debug issues like "Rendered fewer hooks than expected" (Error #300)
 */
export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // This prints the React component stack, which is critical for debugging
    console.group("🚨 App crashed - React Error Boundary");
    console.error("Error:", error);
    console.log("Component stack:", errorInfo.componentStack);
    console.groupEnd();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: 24, 
          fontFamily: "system-ui, -apple-system, sans-serif",
          maxWidth: 600,
          margin: "40px auto"
        }}>
          <h1 style={{ color: "#dc2626", marginBottom: 16 }}>Something went wrong</h1>
          <p style={{ color: "#6b7280", marginBottom: 16 }}>
            An unexpected error occurred. Please refresh the page to try again.
          </p>
          <details style={{ 
            background: "#f3f4f6", 
            padding: 12, 
            borderRadius: 8,
            fontSize: 14
          }}>
            <summary style={{ cursor: "pointer", fontWeight: 500 }}>
              Error details (for developers)
            </summary>
            <pre style={{ 
              marginTop: 8, 
              whiteSpace: "pre-wrap", 
              wordBreak: "break-word",
              color: "#991b1b"
            }}>
              {this.state.error?.message}
            </pre>
            <p style={{ marginTop: 8, color: "#6b7280" }}>
              Open DevTools Console to see the full component stack trace.
            </p>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              padding: "10px 20px",
              background: "#0a1628",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
